import React, { useState, useEffect, useCallback, useRef } from "react";
import { Switch, Row, Input, Button, Modal, Text, Loading } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { fetchWithJson } from "../../lib/api";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import { error, success } from "../../lib/userAlerts";
import debounce from 'lodash/debounce';

const AutoClose = () => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const userId = mstore.get("user", "id");
  const companyId = mstore.get("user", "company_id");

  const [state, setState] = useState({
    is_auto_close_enabled: false,
    fetching: false,
    autoCloseInterval: 5,
    numberOfSeats: 1,
    active_users: 1,
    seats: 1,
    isOpen: false,
  });

  // Create debounced function with useRef to persist across renders
  const debouncedUpdateRef = useRef(null);

  // Initialize debounced function once
  useEffect(() => {
    debouncedUpdateRef.current = debounce(async (interval, enabled) => {
      try {
        await fetchWithJson(endpoint.dc_update_auto_close, {
          auto_close_interval: interval,
          company_id: companyId,
          is_auto_close_enabled: enabled,
        });
        success("You successfully saved auto-close interval");
      } catch (err) {
        error("Oops! error occurred during save auto close interval!");
        console.error(err.message);
      }
    }, 1000); // 1 second debounce

    // Cleanup function
    return () => {
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.cancel();
      }
    };
  }, [companyId]);

  const fetchAutoCloseDetails = useCallback(async () => {
    setState(prev => ({ ...prev, fetching: true }));
    try {
      const response = await fetchWithJson(endpoint.fetchSeatsAndAutoClose, { company_id: companyId });
      const data = await response.json();
      setState(prev => ({
        ...prev,
        is_auto_close_enabled: data.is_auto_close_enabled,
        autoCloseInterval: data.auto_close_interval,
        numberOfSeats: data.seats,
        active_users: data.active_users,
        seats: data.seats,
        fetching: false,
      }));
    } catch (err) {
      error("Oops! error occurred during fetching auto-close details!");
      console.error(err.message);
      setState(prev => ({ ...prev, fetching: false }));
    }
  }, [companyId]);

  useEffect(() => {
    fetchAutoCloseDetails();
  }, [fetchAutoCloseDetails, clientId]);

  const handleValueChange = (e) => {
    const valueAsString = e.target.value;
    const valueAsNumber = parseFloat(valueAsString);

    if (!isNaN(valueAsNumber) && isFinite(valueAsString)) {
      if (valueAsNumber < 5 || valueAsNumber > 30 || valueAsString.includes("-")) {
        error("Kindly set an autoclose interval of more than 5 days and less than 31 days");
      } else {
        setState(prev => ({ ...prev, autoCloseInterval: valueAsNumber }));
        // Use debounced function for interval changes
        if (debouncedUpdateRef.current) {
          debouncedUpdateRef.current(valueAsNumber, state.is_auto_close_enabled);
        }
      }
    } else {
      error("Only numbers allowed!");
    }
  };

  const handleSwitch = async () => {
    const newEnabledState = state.is_auto_close_enabled === 1 ? 0 : 1;
    
    setState(prev => ({
      ...prev,
      is_auto_close_enabled: newEnabledState
    }));
    
    // For switch toggle, call immediately (no debounce needed)
    try {
      await fetchWithJson(endpoint.dc_update_auto_close, {
        auto_close_interval: state.autoCloseInterval,
        company_id: companyId,
        is_auto_close_enabled: newEnabledState,
      });
      
      if (newEnabledState === 1) {
        success("You successfully activated the Auto Close feature");
      } else {
        success("You successfully deactivated the Auto Close feature");
      }
    } catch (err) {
      error("Oops! error occurred");
      console.error(err.message);
    }
  };

  const updateSeats = useCallback(debounce(async () => {
    // Validate before making the API call
    if (state.active_users > Number(state.seats)) {
      error("Number of active users cannot be greater than number of seats");
      setState(prev => ({
        ...prev,
        isOpen: false,
        seats: prev.numberOfSeats
      }));
      return;
    }

    success("Updating the number of seats. Hang on .. ");
    setState(prev => ({ ...prev, isOpen: false, fetching: true }));

    try {
      const response = await fetchWithJson(endpoint.dc_update_seats, {
        number_of_seats: Number(state.seats),
        company_id: companyId,
        user_id: userId,
      });

      const data = await response.json();

      if (response.status === 422) {
        throw new Error("Number of active users greater than number of seats");
      }

      setState(prev => ({
        ...prev,
        fetching: false,
        numberOfSeats: data.seats,
        seats: data.seats, // Sync input field with server response
        active_users: data.active_users
      }));
    } catch (err) {
      error("Sorry we are unable to process your request.");
      console.error(err.message);
      setState(prev => ({ ...prev, fetching: false }));
    }
  }, 300), [state.seats, state.active_users, companyId, userId]);

  const handleValueChangeSeats = (e) => {
    const valueAsString = e.target.value;
    setState(prev => ({ ...prev, seats: valueAsString }));
    
    debounce(() => {
      setState(prev => ({ ...prev, isOpen: true }));
    }, 1000)();
  };

  const closeDialog = () => {
    setState(prev => ({
      ...prev,
      isOpen: false,
      seats: prev.numberOfSeats
    }));
  };

  const renderSeats = () => {
    const seatVal = parseFloat(Math.min(100, 100 * (state.active_users / state.numberOfSeats)).toFixed(2));
    const barColor = seatVal === 100 ? "text-error" : "text-primary";

    return (
      <div className="flex items-center justify-between gap-8">
        {/* Left: Seat Management Form */}
        <div className="flex flex-col gap-4 flex-1">
          <h3 className="text-lg font-semibold mb-2">Seats Management</h3>
          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-600 min-w-[120px]">Seats Provisioned</label>
            <Input
              min={0}
              type='number'
              width="100px"
              size='lg'
              className="mr3"
              underlined
              css={{ $$inputBorderRadius: "0", h: 30 }}
              shadow={false}
              bordered={true}
              rounded={false}
              value={state.seats}
              onChange={handleValueChangeSeats}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Active Users:</span>
            <span className="font-medium">{state.active_users}</span>
          </div>
        </div>
        {/* Right: Radial Progress */}
        <div className="flex flex-col items-center min-w-[140px]">
          <div
            className={`radial-progress ${barColor}`}
            style={{ "--value": seatVal, "--size": "8rem", "--thickness": "4px" }}
            role="progressbar"
          >
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold">{seatVal}%</span>
              <span className="text-sm text-gray-600">
                {state.active_users} of {state.numberOfSeats} Used
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-100">
      <div className="flex flex-wrap justify-between mt3">
        {/* Auto-close Section */}
        <div className="ml2 p-4 pa border-2 border-dotted shadow-lg rounded-xl " style={{ width: '49%' }}>
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold mb-2">Auto-close</h3>
            
            <div className="flex items-center justify-between py-2">
              <label className="text-sm text-gray-600">Enable / Disable feature</label>
              <Switch
                size='sm'
                checked={state.is_auto_close_enabled === 1}
                onChange={handleSwitch}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Auto close interval (days)</label>
              <Input
                type='number'
                width="70px"
                size='lg'
                underlined
                min={5}
                max={30}
                disabled={!state.is_auto_close_enabled}
                value={state.autoCloseInterval}
                onChange={handleValueChange}
                css={{ $$inputBorderRadius: "0", h: 30 }}
                shadow={false}
                bordered={true}
                rounded={false}
              />
            </div>
          </div>
        </div>

        {/* Seats Management Section */}
        <div className="border-2 border-dotted p-4 shadow-lg rounded-xl " style={{ width: '49%' }}>
          {state.fetching ? (
            <Loading css={{ m: 30 }}>Fetching seat count</Loading>
          ) : (
            renderSeats()
          )}
          
          <Modal
            width="480px"
            open={state.isOpen}
            onClose={closeDialog}
            blur
            closeButton
            css={{ br: 0 }}
          >
            <Modal.Header>
              <Text size={24}>Number of seats has been changed</Text>
            </Modal.Header>

            <Modal.Body>
              <Row justify='space-between'>
                <div className="f4">New value</div>
                <div className="f4 fw8 mr3">{state.seats}</div>
              </Row>

              <Row justify='space-between'>
                <div className="f4">Old value</div>
                <div className="f4 fw8 mr3">{state.numberOfSeats}</div>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button css={{ br: 0 }} auto onClick={updateSeats}>
                Update
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default AutoClose;
